import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// ── ErrorBoundary ──────────────────────────────────────────────────────────────
// Catches any render-time error in its child tree and shows a recoverable
// screen instead of letting React unmount everything to a blank page.
// This is the safety net for the whole app — wraps the router in App.js.

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught render error:', error, info);
    this.setState({ errorInfo: info });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    if (this.props.onReset) this.props.onReset();
  };

  handleReload = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100vh', padding: 32, background: 'var(--bg-base, #0A0F1E)', textAlign: 'center', gap: 14,
          overflowY: 'auto',
        }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={26} color="#EF4444" />
          </div>
          <p style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--text-primary, #fff)' }}>Something went wrong</p>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted, #9CA3AF)', maxWidth: 300 }}>
            This screen ran into an unexpected error. Your data is safe — try going back, or reload the app.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={this.handleReset} style={{
              padding: '10px 20px', background: 'var(--bg-elevated, #1A2436)', border: '1px solid var(--border, #2A3548)',
              borderRadius: 10, color: 'var(--text-primary, #fff)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
            }}>
              Go Back
            </button>
            <button onClick={this.handleReload} style={{
              padding: '10px 20px', background: '#3B82F6', border: 'none',
              borderRadius: 10, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <RefreshCw size={15} /> Reload App
            </button>
          </div>

          {/* On-screen error details — no DevTools required. Tap to expand,
              then screenshot this section directly when reporting a bug. */}
          <button
            onClick={() => this.setState({ showDetails: !showDetails })}
            style={{
              marginTop: 18, background: 'none', border: 'none', color: 'var(--text-muted, #9CA3AF)',
              fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
            }}
          >
            {showDetails ? 'Hide' : 'Show'} error details
          </button>
          {showDetails && (
            <div style={{
              width: '100%', maxWidth: 500, textAlign: 'left', background: 'var(--bg-card, #111827)',
              border: '1px solid var(--border, #2A3548)', borderRadius: 10, padding: 14,
              fontFamily: 'monospace', fontSize: '0.6875rem', color: '#F87171',
              overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              <p style={{ fontWeight: 700, marginBottom: 8 }}>{error?.toString()}</p>
              {error?.stack && (
                <p style={{ color: 'var(--text-muted, #9CA3AF)', marginBottom: 8 }}>{error.stack}</p>
              )}
              {errorInfo?.componentStack && (
                <>
                  <p style={{ fontWeight: 700, color: 'var(--text-muted, #9CA3AF)', marginTop: 10, marginBottom: 4 }}>Component stack:</p>
                  <p style={{ color: 'var(--text-muted, #9CA3AF)' }}>{errorInfo.componentStack}</p>
                </>
              )}
            </div>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
